import { useDisclosure } from '@mantine/hooks';
import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  ActionIcon, Button, Container, Flex, Modal, ScrollArea, Text,
} from '@mantine/core';
import prepareTranslate from '@/helpers/dictionary';
import fileCommisionDeviationMutations from '@/api/mutitations/fileCommisionDeviations';
import processInitiatorMutations from '@/api/mutitations/processInitiator';
import { IconChevronRight } from '@tabler/icons-react';
import styles from './style.module.css';
import SectionView from '../SectionView';
import CustomModal from '../Modal/Index';

const RowErrorsModal = (props) => {
  const {
    rowsErrors,
    onClose,
    rule,
  } = props;

  const t = prepareTranslate();

  const { mutateAsync: createFileCommisionDeviationMutation } = fileCommisionDeviationMutations
    .useCreate();

  const { mutateAsync: createAndDeleteProcessInitMutate } = processInitiatorMutations
    .useCreateAndDelete();

  const [opened, { close }] = useDisclosure(true);
  const [activeErrorIndex, setActiveErrorIndex] = useState(0);
  const [loadingApprove, setLoadingApprove] = useState(false);
  const [loadingReject, setLoadingReject] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [finish, setFinish] = useState(false);

  const row = rowsErrors[activeErrorIndex] || {};

  const handleConfirm = async (approved) => {
    setErrorMessage('');
    const setLoading = approved ? setLoadingApprove : setLoadingReject;
    setLoading(true);

    const saveObj = {
      company: row.companyId,
      product: row.productId,
      rowNumber: row.rowNumber,
      expectedValue: row.expectedValue,
      actualValue: row.actualValue,
      year: row.year,
      policyNumber: row.policyNumber,
      deviationValue: row.difference,
      column: row.column,
      isApproved: approved,
    };

    try {
      await createFileCommisionDeviationMutation(saveObj);

      if (activeErrorIndex >= rowsErrors.length - 1) {
        const { year, month } = rowsErrors[0];

        setFinish(true);
        close();

        const processInitiatorObj = {
          year,
          rule: rule.id,
          month,
          trigger: 'desired',
        };

        try {
          await createAndDeleteProcessInitMutate(processInitiatorObj);
        } catch (err) {
          setErrorMessage('התרחשה שגיאה בתהליך');
        }
      } else {
        setActiveErrorIndex((prev) => prev + 1);
      }
    } catch (error) {
      setErrorMessage('התרחשה שגיאה בתהליך');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    close();
    onClose();
  };

  const actions = (
        <Flex justify="space-between" align="center" w="100%" mt="xl">
            <Flex gap="lg">
                <Button size="sm" onClick={() => handleConfirm(true)} loading={loadingApprove} >
                  {t('components.rowErrorsModal.rowModal.saveButton')}
              </Button>
                <Button size="sm" variant="outline" onClick={() => handleConfirm(false)} loading={loadingReject} >
                    {t('components.rowErrorsModal.rowModal.cancelButton')}
                </Button>
            </Flex>
            <Flex>
              <Button size="sm" variant="outline" onClick={handleClose}>
                {t('components.rowErrorsModal.rowModal.cancelProcessButton')}
                </Button>
            </Flex>
        </Flex>);

  return (
        <>
            {!finish ? (
                <Modal.Root
                    opened={opened}
                    onClose={handleClose}
                    scrollAreaComponent={ScrollArea.Autosize}
                    size="xl"
                    p="xl"
                    zIndex={300}
                    centered
                    closeOnClickOutside={false}
                >
                    <Modal.Overlay />
                    <Modal.Content>
                        <Modal.Header className={styles.modal_header}>
                            <div className={styles.header_content}>
                                {activeErrorIndex > 0 && (
                                    <ActionIcon
                                        color="root"
                                        variant="transparent"
                                        onClick={() => setActiveErrorIndex((prev) => prev - 1)}
                                        className={styles.back_button}
                                    >
                                        <IconChevronRight />
                                    </ActionIcon>
                                )}
                            </div>
                        </Modal.Header>
                        <Modal.Body>
                            <Container fluid>
                                <Text size="xs">
                                    {`${rowsErrors.length} / ${activeErrorIndex + 1}`}
                                </Text>
                                <Text c="var(--mantine-color-brand-5)" fw="bold">
                                    {t('components.rowErrorsModal.rowModal.title', [rule.title])}
                                </Text>
                                <SectionView
                                    lines={[
                                      {
                                        title: t('components.rowErrorsModal.rowModal.lines.row'),
                                        value: row.rowNumber,
                                      },
                                      {
                                        title: t('components.rowErrorsModal.rowModal.lines.column'),
                                        value: row.column,
                                      },
                                      {
                                        title: t('components.rowErrorsModal.rowModal.lines.actualValue'),
                                        value: row.actualValue,
                                      },
                                      {
                                        title: t('components.rowErrorsModal.rowModal.lines.expectedValue'),
                                        value: row.expectedValue,
                                      },
                                    ]}
                                />

                                {errorMessage && (
                                    <Text c="red" mb="md">
                                        {errorMessage}
                                    </Text>
                                )}

                                {actions}
                            </Container>
                        </Modal.Body>
                    </Modal.Content>
                </Modal.Root>
            ) : (
                <CustomModal
                    opened
                      actions={
                          [
                              <Button key="close" onClick={handleClose}>{t('global.close')}</Button>,
                          ]
                      }
                    label={t('components.rowErrorsModal.finishModal.label', [rule.title])}
                    message={t('components.rowErrorsModal.finishModal.message')}
               />
            )}
        </>
  );
};

RowErrorsModal.propTypes = {
  rowsErrors: PropTypes.arrayOf(PropTypes.shape({
    year: PropTypes.number,
    month: PropTypes.number,
    companyId: PropTypes.string,
    productId: PropTypes.string,
    rowNumber: PropTypes.number,
    expectedValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    actualValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    policyNumber: PropTypes.string,
    difference: PropTypes.number,
    column: PropTypes.string,
  })).isRequired,
  onClose: PropTypes.func.isRequired,
  rule: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
  }).isRequired,
};

export default RowErrorsModal;
